import { Component } from '@angular/core';
import { StateService } from './services/state/state.service';
import { DialogService } from '@libs/index';
import { CookiesDialogComponent } from './pages/components/cookies-dialog/cookies-dialog.component';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@libs/index';
import { NavigationEnd, Router } from '@angular/router';
import { LoadingSplashScreenService } from './services/loading-splash-screen/loading-splash-screen.service';
import { User } from '@libs/index';
import { RwgData } from './models/interfaces/RwgData.interface';
import { combineLatest, merge, Subject } from 'rxjs';
import { UserService } from '@libs/index';
import { ErrorHandlerServiceService } from '@libs/index';
import { IframeService } from './services/iframe/iframe.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends BaseComponent {
  private readonly AUTO_RELOAD_COUNTDOWN_IN_MS = 5000;
  private isAlreadyCheckedCookies$$ = new Subject();

  constructor(
    public iframeService: IframeService,
    private router: Router,
    private stateService: StateService,
    private dialogService: DialogService,
    private loadingSplashScreenService: LoadingSplashScreenService,
    private errorHandlerServiceService: ErrorHandlerServiceService,
    private userService: UserService
  ) {
    super();

    this.init();

    combineLatest([this.stateService.user$, this.stateService.rwgData$])
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(([user, rwgData]) => this.updateRwgDataInUser(user, rwgData));
  }

  private async init() {
    await this.loadingSplashScreenService.present();

    if (this.iframeService.isInIframe()) {
      this.iframeService.iframeModeEnabled$
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe((isEnabled) => {
          if (isEnabled) {
            this.loadingSplashScreenService.dismiss();
          }
        });
    } else {
      // to avoid stuck in loading screen
      const reloadAppTimeout = setTimeout(() => {
        window.location.reload();
      }, this.AUTO_RELOAD_COUNTDOWN_IN_MS);

      const stopSubscribeNavigationConditions$ = merge(
        this.componentDestroyed,
        this.isAlreadyCheckedCookies$$
      );

      this.router.events
        .pipe(takeUntil(stopSubscribeNavigationConditions$))
        .subscribe((event) => {
          if (event instanceof NavigationEnd) {
            clearTimeout(reloadAppTimeout);
            this.loadingSplashScreenService.dismiss();

            this.checkCookies();

            this.isAlreadyCheckedCookies$$.next();
            this.isAlreadyCheckedCookies$$.unsubscribe();
          }
        });
    }
  }

  private checkCookies() {
    if (!this.stateService.getCookiesConfig()?.essential) {
      this.openCookiesDialog();
    }
  }

  private openCookiesDialog(): void {
    this.dialogService.openDialog(
      CookiesDialogComponent,
      this.dialogService.makeUnableToClose(
        this.dialogService.createDefaultDialogConfig(null)
      )
    );
  }

  private async updateRwgDataInUser(user: User, rwgData: RwgData) {
    if (!user?.id || !rwgData) {
      return;
    }

    try {
      const isSuccess = await this.userService.updateUser({
        ...user,
        rwgToken: rwgData.rwgToken,
        rwgMerchantId: rwgData.rwgMerchantId,
      });

      if (isSuccess) {
        this.stateService.setRwgData(null);
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    }
  }
}
